import React, { FC, useEffect } from "react";

import { DataViewKey, useShare } from "@keepeek/commons";
import {
  KSearchWidget as KSearchWidgetComponent,
  SanitizeHtml,
  withSuspense,
  useThemeColors,
} from "@keepeek/refront-components";
import { Typography } from "@mui/material";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { KIcon } from "../../../components/common/KIcon";
import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import { Button } from "../../../models/configuration/components/searchWidget";
import { ResourceDef } from "../../../models/configuration/definitions/resourceDef";
import { Labels, TranslationSchema } from "../../../models/configuration/labels";
import { Key } from "../../../models/configuration/theme";
import { useConfiguration } from "../../../providers/config/hooks";
import { useImageOptim } from "../../../providers/config/hooks/useImageOptim";
import { configSectionThemeAssetSelector } from "../../../providers/config/selectors";
import useFrontEditLabelWrapper from "../../../providers/frontEdit/hooks/useFrontEditLabelWrapper";
import { useSearchAutocomplete } from "../../../providers/searchAutocomplete/hooks";
import { PagesPath } from "../../App/utils";
import EditableSanitizeHtml from "../../FrontEdit/EditableSanitizeHtml";
import ImageOptim from "../../ImageOptim";
import { WidgetUIState } from "../atoms";
import { useWidgetState } from "../hooks";
import { KWidgetProps } from "../models";
import { parseOptionsInternalUrl } from "../utils/parseInternalUrl";

export type KSearchWidgetProps = {
  widgetId: string;
  height: string;
  showBackgroundUrl: boolean;
  backgroundUrl?: string;
  backgroundUrlResource?: ResourceDef;
  backgroundColor?: string;
  button?: Button;
  borderRadius?: string;
  inputBorderRadius?: string;
  fields?: string[];
  content?: TranslationSchema[];
  autoFocus?: boolean;
  searchBarBackgroundColor?: string;
  searchBarTextColor?: string;
} & KWidgetProps;

const KSearchWidget: FC<React.PropsWithChildren<KSearchWidgetProps>> = function ({
  widgetId,
  height,
  showBackgroundUrl,
  backgroundUrl,
  backgroundUrlResource,
  backgroundColor,
  borderRadius,
  button,
  inputBorderRadius,
  fields,
  content,
  autoFocus = false,
  searchBarBackgroundColor,
  searchBarTextColor,
  type,
}) {
  const router = useRouter();
  const { convertColor } = useThemeColors();
  const { isShare } = useShare(router.pathname, router.query.tokenId);

  const { setWidgetState } = useWidgetState({
    id: widgetId,
    type,
  });

  const {
    handleQueryUpdate,
    selectOption,
    autoCompleteVals,
    query,
    loading,
    onSearch,
    setKSplitValue,
    kSplitValues,
    kSplitValue,
  } = useSearchAutocomplete({
    widgetId,
    resetSearch: !router.pathname.includes(PagesPath.SEARCH_PAGE),
    fields,
    dataViewKey: DataViewKey.SEARCH,
  });
  const { FrontEditComponentsLabelWrapper } = useFrontEditLabelWrapper();
  const { t } = useTranslation();
  const [background] = useRecoilValue<string[]>(
    configSectionThemeAssetSelector(Key.ImageBackgroundSearchWidget),
  );

  const backgroundSrc = backgroundUrlResource?.url || backgroundUrl;

  const Title: FC<React.PropsWithChildren<{}>> = function () {
    if (content?.some((c) => c.language === router.locale)) {
      return (
        <SanitizeHtml
          sanitizeOptions={{ allowedAttributes: false, allowedTags: false }}
          parseOptions={parseOptionsInternalUrl}
          html={content?.find((c) => c.language === router.locale)?.value ?? ""}
        />
      );
    }
    return (
      <Typography
        component="h1"
        variant="h4"
        style={{
          color: "#fff",
          textAlign: "center",
        }}
      >
        <EditableSanitizeHtml labelKey={"home.widget.search.title"} />
      </Typography>
    );
  };

  useEffect(() => {
    setWidgetState({ state: WidgetUIState.RENDERED });
  }, [setWidgetState]);

  const { shouldOptimize, loading: loadingOptimization } = useImageOptim();
  const configSection = useConfiguration<Labels[]>(CONFIGURATION_SECTION_PATH.LABELS);
  const labelLocale: TranslationSchema | undefined = configSection
    ?.find((l) => l.key === "home.widget.search.input.button.label")
    ?.translation?.find((t) => t.language.toLowerCase() === router.locale?.toLowerCase());

  if (isShare) {
    return <></>;
  }

  return (
    <KSearchWidgetComponent
      style={{
        height,
        backgroundColor: convertColor(backgroundColor),
        borderRadius: borderRadius ? `0 0 ${borderRadius} ${borderRadius}` : "unset",
      }}
      query={query}
      setQuery={handleQueryUpdate}
      title={<Title />}
      backgroundUrl={
        !loadingOptimization && !shouldOptimize && showBackgroundUrl
          ? backgroundSrc || background
          : ""
      }
      buttonStyle={button}
      label={t("home.widget.search.input.label") as string}
      placeholder={t("home.widget.search.input.placeholder.label") as string}
      buttonLabel={"home.widget.search.input.button.label"}
      KIcon={KIcon}
      size="medium"
      onSearch={onSearch}
      onOptionSelected={selectOption}
      autoCompleteOptions={autoCompleteVals}
      loading={loading}
      splitValue={kSplitValue}
      splitLabel={t("home.widget.search.input.split.label") as string}
      splitValues={kSplitValues}
      onSplitSelect={setKSplitValue}
      autoFocus={autoFocus}
      inputBorderRadius={inputBorderRadius}
      searchBarBackgroundColor={convertColor(searchBarBackgroundColor)}
      searchBarTextColor={convertColor(searchBarTextColor)}
      backgroundImage={
        showBackgroundUrl &&
        shouldOptimize &&
        !loadingOptimization && (
          <ImageOptim
            src={backgroundSrc || background}
            alt={""}
            fill
            style={{
              objectFit: "cover",
              objectPosition: "50% 50%",
              borderRadius: borderRadius ? `0 0 ${borderRadius} ${borderRadius}` : "unset",
            }}
            priority
          >
            {null}
          </ImageOptim>
        )
      }
      EditableSanitizeHtml={FrontEditComponentsLabelWrapper}
      isSearchButtonEmpty={labelLocale && labelLocale.empty}
    />
  );
};
export default withSuspense(KSearchWidget);
